import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationsAPI } from '../../services/api';
import MobileBottomNav from '../layout/MobileBottomNav';
import { 
  Bell, Check, CheckCheck, Trash2, RefreshCw, ChevronLeft,
  TrendingUp, TrendingDown, Wallet, ShoppingCart, AlertCircle, Info
} from 'lucide-react';

const C = {
  bg: '#0b0e11',
  bgCard: '#1a1d21',
  bgElevated: '#23272b',
  border: '#2a2e33',
  text: '#eaecef',
  textSecondary: '#848e9c',
  textMuted: '#5e6673',
  yellow: '#f0b90b',
  green: '#0ecb81',
  red: '#f6465d',
  blue: '#3b82f6',
  greenBg: 'rgba(14, 203, 129, 0.1)',
  redBg: 'rgba(246, 70, 93, 0.1)',
  yellowBg: 'rgba(240, 185, 11, 0.1)',
  blueBg: 'rgba(59, 130, 246, 0.1)',
};

const NOTIFICATION_ICONS = {
  trade_opened: { icon: TrendingUp, color: C.green, bg: C.greenBg },
  trade_closed: { icon: TrendingDown, color: C.yellow, bg: C.yellowBg },
  spot_buy: { icon: ShoppingCart, color: C.green, bg: C.greenBg },
  spot_sell: { icon: Wallet, color: C.red, bg: C.redBg },
  ido_buy: { icon: ShoppingCart, color: C.yellow, bg: C.yellowBg },
  ido_sell: { icon: Wallet, color: C.yellow, bg: C.yellowBg },
  deposit: { icon: Wallet, color: C.green, bg: C.greenBg },
  withdrawal: { icon: Wallet, color: C.red, bg: C.redBg },
  price_alert: { icon: AlertCircle, color: C.yellow, bg: C.yellowBg },
  system: { icon: Info, color: C.blue, bg: C.blueBg },
  support: { icon: Bell, color: C.blue, bg: C.blueBg },
};

const MobileNotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadNotifications = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const res = await notificationsAPI.getAll(100, 0);
      // Handle different response formats
      const data = res.data || res;
      const notifList = data?.notifications || (Array.isArray(data) ? data : []);
      // Normalize is_read field
      const normalizedList = notifList.map(n => ({
        ...n,
        is_read: n.is_read ?? n.read ?? false
      }));
      setNotifications(normalizedList);
    } catch (err) {
      console.error('Failed to load notifications:', err);
      // If unauthorized, redirect to login
      if (err.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    // Check if token exists before loading
    const token = localStorage.getItem('token');
    if (token) {
      loadNotifications();
    } else {
      setLoading(false);
    }
  }, [loadNotifications]);

  const handleMarkAsRead = async (id) => {
    try {
      await notificationsAPI.markAsRead(id);
      setNotifications(prev => prev.map(n => 
        n.id === id ? { ...n, is_read: true } : n
      ));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await notificationsAPI.delete(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Удалить все уведомления?')) return;
    try {
      await notificationsAPI.deleteAll();
      setNotifications([]);
    } catch (err) {
      console.error('Failed to delete all:', err);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Только что';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} мин назад`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} ч назад`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)} дн назад`;
    
    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: C.bg, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <RefreshCw size={32} color={C.yellow} style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: C.bg, paddingBottom: 100 }}>
      {/* Header */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: C.bg,
        borderBottom: `1px solid ${C.border}`,
        padding: '12px 16px',
        paddingTop: 'max(12px, env(safe-area-inset-top))',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={() => navigate(-1)}
              style={{ background: 'none', border: 'none', padding: 8, cursor: 'pointer' }}
            >
              <ChevronLeft size={24} color={C.text} />
            </button>
            <h1 style={{ color: C.text, fontSize: 18, fontWeight: 700, margin: 0 }}>
              Уведомления
            </h1>
            {unreadCount > 0 && (
              <span style={{
                background: C.red,
                color: '#fff',
                fontSize: 11,
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: 10,
              }}>
                {unreadCount}
              </span>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => loadNotifications(true)}
              style={{
                background: C.bgCard,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                padding: 8,
                cursor: 'pointer',
              }}
            >
              <RefreshCw 
                size={18} 
                color={C.textSecondary} 
                style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }}
              />
            </button>
            
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                style={{
                  background: C.bgCard,
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  padding: 8,
                  cursor: 'pointer',
                }}
              >
                <CheckCheck size={18} color={C.green} />
              </button>
            )}
            
            {notifications.length > 0 && (
              <button
                onClick={handleDeleteAll}
                style={{
                  background: C.bgCard,
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  padding: 8,
                  cursor: 'pointer',
                }}
              >
                <Trash2 size={18} color={C.red} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div style={{ padding: 16 }}>
        {notifications.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px',
            color: C.textSecondary 
          }}>
            <Bell size={48} color={C.textMuted} style={{ marginBottom: 16 }} />
            <div style={{ fontSize: 14 }}>Нет уведомлений</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {notifications.map((notif) => {
              const config = NOTIFICATION_ICONS[notif.type] || NOTIFICATION_ICONS.system;
              const Icon = config.icon;
              
              return (
                <div
                  key={notif.id}
                  style={{
                    background: notif.is_read ? C.bgCard : C.bgElevated,
                    borderRadius: 12,
                    padding: 14,
                    border: `1px solid ${notif.is_read ? C.border : config.color}40`,
                    position: 'relative',
                  }}
                >
                  {!notif.is_read && (
                    <div style={{
                      position: 'absolute',
                      top: 14,
                      right: 14,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: C.yellow,
                    }} />
                  )}
                  
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: config.bg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Icon size={20} color={config.color} />
                    </div>
                    
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        color: C.text, 
                        fontSize: 14, 
                        fontWeight: 600,
                        marginBottom: 4,
                      }}>
                        {notif.title}
                      </div>
                      <div style={{ 
                        color: C.textSecondary, 
                        fontSize: 13,
                        lineHeight: 1.4,
                        marginBottom: 8,
                      }}>
                        {notif.message}
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between' 
                      }}>
                        <span style={{ color: C.textMuted, fontSize: 11 }}>
                          {formatDate(notif.created_at)}
                        </span>
                        
                        <div style={{ display: 'flex', gap: 8 }}>
                          {!notif.is_read && (
                            <button
                              onClick={() => handleMarkAsRead(notif.id)}
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: 4,
                                cursor: 'pointer',
                              }}
                            >
                              <Check size={16} color={C.green} />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(notif.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 4,
                              cursor: 'pointer',
                            }}
                          >
                            <Trash2 size={16} color={C.red} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <MobileBottomNav />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default MobileNotificationsPage;
